import { FormikProps } from 'formik';
import { Dispatch } from 'react';
import { personSearch } from '../../../../global/ducks/person-prc-api';
import { combineDesignationSalution, formatDate } from '../../../../global/helpers/form-data-helper';
import { BaseApiState, YesNoIndicators } from '../../../../global/models/api-model';
import { DependentFormValues } from '../../../account-info/features/dependents/dependent-form-models';
import { MaritalStatusFormValues } from '../../../account-info/features/marital-status/marital-form-models';
import { noSelectPersonId } from '../../models/arc-epi-props';
import { GroupAdminAddFormValues } from '../groups/admin/group-admin-add-form-model';
import { OfficerAddFormValues } from '../institution-officers/officer-add-form-model';
import { getInitialNewHireNonStipValues, getInitialNewHireStipValues, NewHireFormValues } from '../new-hire/models/new-hire-form-model';
import { OSCRRModel, PersonSearchKeys, PersonSearchPayload, OSCRRKeySet, OSCRRRequiredKeys, SearchPerson, OSCRRKeys, PersonSearchParams, PersonSearchState } from './person-search-model';
import { setSearchResults, setHideToast } from './person-search-slice';

export const oSCRRSearchDelay = 200;

export class OSCRRService {

	public static IsWorking: boolean;

	public static latestResults: SearchPerson[];

	public static async SearchHasNewResultsAsync(apiState: BaseApiState, personSearchState: PersonSearchState, matched_person: string = null): Promise<boolean> {

		if (matched_person === noSelectPersonId) return new Promise<boolean>((resolve) => { resolve(false); });

		const resultCount: number = personSearchState.searchResults?.length;

		await OSCRRService.WaitForMillisecondsAsync(oSCRRSearchDelay * 4);
		await OSCRRService.WaitForSearchAsync(apiState);

		const resultCountAfter: number = OSCRRService.latestResults?.length;

		const promise = new Promise<boolean>(
			(resolve) => {
				resolve(resultCountAfter > resultCount);
			});

		return promise;
	}

	public static IsOSCRRWorking(apiState: BaseApiState): boolean {
		const isWorking = (apiState.pendingRequests?.length > 0) && apiState.pendingRequests.some(request => request.toLowerCase().includes('officersPOST'));
		return isWorking || OSCRRService.IsWorking;
	}

	private static InternalWaitForSearch(isWorkingDelegate: () => boolean, resolveCallback: any) {
		if (isWorkingDelegate) {
			if (!isWorkingDelegate()) {
				if (resolveCallback) {
					resolveCallback();
				}
			} else {
				setTimeout(() => {
					OSCRRService.InternalWaitForSearch(isWorkingDelegate, resolveCallback);
				}, 200);
			}
		}
	}

	private static WaitForSearchAsync(apiState: BaseApiState): Promise<void> {
		const promise = new Promise<void>(
			(resolve, reject) => {
				try {
					OSCRRService.InternalWaitForSearch(
						() => {
							return OSCRRService.IsOSCRRWorking(apiState);
						},
						resolve);
				} catch (error) {
					reject(error);
				}
			});

		return promise;
	}

	private static WaitForMillisecondsAsync(milliseconds: number): Promise<void> {
		const promise = new Promise<void>(
			(resolve) => {
				setTimeout(() => {
					resolve();
				}, milliseconds);
			});

		return promise;
	}
}

export function handleOSCRR(dispatch: Dispatch<any>, personSearchKey: PersonSearchKeys, values: OSCRRModel, officerSearch: boolean) {
	const nonEmptyKeys = Object.keys(values).filter(key => !!values[key]).filter(key => OSCRRKeySet.has(key));

	const requiredKeys = nonEmptyKeys.filter(key => OSCRRRequiredKeys.has(key));

	const clearResults = () => {
		OSCRRService.IsWorking = false;
		OSCRRService.latestResults = null;
		dispatch(setSearchResults([], personSearchKey));
	};

	if (requiredKeys.length < 3) {
		clearResults();
		return;
	}

	const searchValues: PersonSearchParams = {};
	for (let i = 0; i < nonEmptyKeys.length; i += 1) {
		const key = nonEmptyKeys[i];

		// map form fields to correct search params
		switch (key) {
			case OSCRRKeys.date_of_birth:
				searchValues[key] = formatDate(values[key]);
				break;
			case OSCRRKeys.personal_email_address:
			case OSCRRKeys.office_email_address:
				if (officerSearch) searchValues.email = values[OSCRRKeys.personal_email_address] || values[OSCRRKeys.office_email_address];
				searchValues.email_address = values[OSCRRKeys.personal_email_address] || values[OSCRRKeys.office_email_address];
				break;
			case OSCRRKeys.tax_id:
				searchValues.ssn = values[key];
				break;
			case OSCRRKeys.mailing_city:
				searchValues.city = values[key];
				break;
			case OSCRRKeys.mailing_state:
				searchValues.state = values[key];
				break;
			case OSCRRKeys.mailing_postal_code:
				searchValues.postal_code = values[key];
				break;
			case OSCRRKeys.mailing_country:
				searchValues.country = values[key];
				break;
			default:
				searchValues[key] = values[key];
				break;
		}
	}

	const payload: PersonSearchPayload = {
		search: {
			...searchValues,
		},
	};

	dispatch(personSearch(payload, officerSearch,
		(response) => { // success
			OSCRRService.IsWorking = false;
			OSCRRService.latestResults = response.data;
			dispatch(setSearchResults(response.data, personSearchKey));
			dispatch(setHideToast(false, personSearchKey));
		}, () => { // failure
			clearResults();
		}));
}

export function handleOSCRRBlur(dispatch: Dispatch<any>, formProps: FormikProps<OSCRRModel>, personSearchKey: PersonSearchKeys, fieldName: string, value: string, officerSearch = false) {
	if (personSearchKey !== PersonSearchKeys.None) {
		const newValues = {
			...formProps.values,
			[fieldName]: value,
		};

		OSCRRService.IsWorking = true;

		setTimeout(() => {
			handleOSCRR(dispatch, personSearchKey, newValues, officerSearch);
		}, oSCRRSearchDelay);
	}
}

export function transformSearchPersonToPersonFields(person: SearchPerson) {
	return {
		designation_salutation: combineDesignationSalution(person.designation, person.salutation),
		first_name: person.first_name,
		middle_name: person.middle_name,
		last_name: person.last_name,
		suffix: person.suffix,
		gender: person.gender,
		date_of_birth: person.masked_date_of_birth,
		tax_id: person.masked_ssn,
	};
}

export function transformSearchPersonToMailingAddressFields(person: SearchPerson) {
	return {
		mailing_address_1: person?.mailing_address?.masked_address1,
		mailing_address_2: person?.mailing_address?.masked_address2,
		mailing_address_3: person?.mailing_address?.masked_address3,
		mailing_country: person.mailing_address?.country,
		mailing_city: person.mailing_address?.city,
		mailing_state: person.mailing_address?.state,
		mailing_postal_code: person.mailing_address?.postal_code,
	};
}

export function transformSearchPersonToPhoneFields(person: SearchPerson) {
	return {
		business_phone_formated: person.business_number?.phone_number_combined,
		home_phone_formated: person.home_number?.phone_number_combined,
		mobile_phone_formated: person.mobile_number?.phone_number_combined,
	};
}

export function transformSearchPersonToEmailFields(person: SearchPerson) {
	return {
		office_email_address: person.office_email?.email_address,
		personal_email_address: person.personal_email?.email_address,
	};
}

export function transformSearchPersonToMaritalFormValues(person: SearchPerson, maritalStatus: string): MaritalStatusFormValues {

	if (!person) return {} as MaritalStatusFormValues;

	const values: MaritalStatusFormValues = {
		marital_status: maritalStatus,
		marital_status_effective_date: null,

		...transformSearchPersonToPersonFields(person),
		...transformSearchPersonToMailingAddressFields(person),
		...transformSearchPersonToPhoneFields(person),
		...transformSearchPersonToEmailFields(person),
		matched_person: person?.guid,
		attestation1: false,
		attestation2: false,
	} as MaritalStatusFormValues;

	return values;
}

export function transformSearchPersonToDependentFormValues(person: SearchPerson, dependentType: string, childType: string, startDate: string): DependentFormValues {

	if (!person) return {} as DependentFormValues;

	const values: DependentFormValues = {
		dependent_type: dependentType,
		child_type: childType,
		start_date: startDate,
		matched_person: person.guid,

		...transformSearchPersonToPersonFields(person),
		...transformSearchPersonToMailingAddressFields(person),
		...transformSearchPersonToPhoneFields(person),
		...transformSearchPersonToEmailFields(person),
	} as DependentFormValues;

	return values;
}

export function transformSearchPersonToGroupAdminAddFormValues(person: SearchPerson, adminType: string, nonEpiscopal: YesNoIndicators): GroupAdminAddFormValues {

	if (!person) return {} as GroupAdminAddFormValues;

	const values: GroupAdminAddFormValues = {

		adminType,
		nonEpiscopal,
		matched_person: person.guid,

		...transformSearchPersonToPersonFields(person),
		...transformSearchPersonToMailingAddressFields(person),
		...transformSearchPersonToPhoneFields(person),
		...transformSearchPersonToEmailFields(person),
	} as GroupAdminAddFormValues;

	return values;
}

export function transformSearchPersonToOfficerAddFormValues(person: SearchPerson, officerType: string, nonEpiscopal: YesNoIndicators, withAccessTo: string[]): OfficerAddFormValues {

	if (!person) return {} as OfficerAddFormValues;

	const values: OfficerAddFormValues = {
		withAccessTo,
		officerType,
		nonEpiscopal,
		matched_person: person.guid,

		...transformSearchPersonToPersonFields(person),
		...transformSearchPersonToMailingAddressFields(person),
		...transformSearchPersonToPhoneFields(person),
		...transformSearchPersonToEmailFields(person),
	} as OfficerAddFormValues;

	return values;
}

export function transformSearchPersonToNewHireFormValues(person: SearchPerson, nonStip: boolean): NewHireFormValues {

	if (!person) return {} as NewHireFormValues;

	const values: NewHireFormValues = {
		...transformSearchPersonToPersonFields(person),
		...transformSearchPersonToMailingAddressFields(person),
		...transformSearchPersonToPhoneFields(person),
		...transformSearchPersonToEmailFields(person),

		...(nonStip ? getInitialNewHireNonStipValues() : getInitialNewHireStipValues()),
		matched_person: person.guid,
	} as NewHireFormValues;

	return values;
}

export function shouldContinueButtonBeDisabled(selectedPersonId: string, searchResults: SearchPerson[], hasCurrentEmployee: boolean = false): boolean {
	return searchResults?.length > 0 ? !selectedPersonId : hasCurrentEmployee;
}